import React from "react";
import { formatNumber } from "@/lib/utils";
import ISOPrintHeader from "@/components/shared/print/ISOPrintHeader";
import { PrintDocumentTitle } from "@/components/shared/print";
import {
  QcPrintConclusion,
  QcPrintCriteriaTable,
  QcPrintDefects,
  QcPrintInfoGrid,
  QcPrintLegacy,
  QcPrintSamplingRow,
  QcPrintSignatures,
  checklistOf,
} from "./qc-print-blocks";

interface QCMaterialPrintTemplateProps {
  data?: any;
  config?: any;
}

/** BM-QC-01 — Phiếu kiểm tra vật tư đầu vào (1 trang A4 khi ≤ 15 chỉ tiêu) */
export default function QCMaterialPrintTemplate({ data = {}, config }: QCMaterialPrintTemplateProps) {
  const docId =
    data?.materialQcId ||
    data?.code ||
    (data?.id?.length > 20 ? `IQC-${data.id.slice(0, 8).toUpperCase()}` : data?.id) ||
    "";
  const unit = data.unit || data.technicalParams?.unit || data.material?.unit || "";
  const lotSize = Number(data.lotSize ?? data.quantity ?? data.qtyReceived ?? 0);
  const isLegacy = !data.inspectionMode;

  return (
    <div
      className="p-[8mm] text-black leading-tight text-[10pt] bg-white w-full max-w-[210mm] mx-auto"
      style={{ fontFamily: "'Times New Roman', Times, serif" }}
      id="print-area"
    >
      <ISOPrintHeader config={config} />

      <PrintDocumentTitle
        title="PHIẾU KIỂM TRA VẬT TƯ ĐẦU VÀO"
        docNo={docId}
        docNoLabel="Số phiếu"
        extraMeta="BM-QC-01"
        date={data.checkTime || data.checkDate || data.createdAt || new Date()}
        dateLabel="Ngày kiểm"
      />

      <QcPrintInfoGrid
        items={[
          ["Vật tư", data.material?.name || data.materialName || data.item_name],
          ["Mã vật tư", data.material?.materialId || data.material?.code || data.materialCode],
          ["Nhóm vật tư", data.materialCategory],
          ["Nhà cung cấp", data.vendor?.name || data.supplierName || data.vendorName],
          ["Đơn mua", data.purchaseOrder?.poId || data.poNumber || data.receiptDocNo],
          ["Số lô NCC", data.batchNumber || data.batch_no],
          ["Số CoA", `${data.coaNumber || "—"}${data.coaVerified === false ? "" : " (đã đối chiếu)"}`],
          ["Mẫu kiểm tra", data.template?.code],
        ]}
      />

      <QcPrintSamplingRow
        lotSize={lotSize}
        unit={unit}
        mode={data.inspectionMode}
        level={data.aqlLevel}
        aqlMajor={data.aqlMajor ?? data.aqlStandard}
        aqlMinor={isLegacy ? null : data.aqlMinor}
        sampleSize={data.sampleSize}
        acMajor={data.acMajor ?? data.acValue}
        reMajor={data.reMajor ?? data.reValue}
        acMinor={data.acMinor}
        reMinor={data.reMinor}
      />

      <QcPrintCriteriaTable items={checklistOf(data.checklist)} />

      {isLegacy && (
        <QcPrintLegacy
          rows={[
            ["Định lượng", data.actualGsm ?? data.grammage ? `${data.actualGsm ?? data.grammage} g/m²` : null],
            ["Độ dày", data.actualCaliper ?? data.caliper ? `${data.actualCaliper ?? data.caliper} mm` : null],
            ["Độ ẩm", data.actualMoisture ?? data.moisture ? `${data.actualMoisture ?? data.moisture} %` : null],
            ["Cobb60", data.cobbValue ? `${data.cobbValue} g/m²` : null],
          ]}
        />
      )}

      <QcPrintDefects items={data.defectDetails || data.defectBreakdown} />

      <QcPrintConclusion
        status={data.status}
        counts={{ critical: data.criticalDefects || 0, major: data.majorDefects || 0, minor: data.minorDefects || 0 }}
        reMajor={data.reMajor ?? data.reValue}
        reMinor={data.reMinor}
        overrideReason={data.overrideReason}
        notes={data.notes}
        extra={
          <>
            SL nhập kho <b>{formatNumber(Number(data.acceptedQty ?? data.qtyAccepted ?? lotSize))}</b> · SL loại{" "}
            <b>{formatNumber(Number(data.rejectedQty ?? data.qtyRejected ?? 0))}</b>
            {unit ? ` ${unit}` : ""}
          </>
        }
      />

      <QcPrintSignatures
        columns={[
          { title: "Người kiểm", hint: "(Ký, ghi rõ họ tên)", name: data.inspector?.fullName || data.inspectorName },
          { title: "Kho nhận", hint: "(Ký tiếp nhận)" },
          { title: "Trưởng QC", hint: "(Ký duyệt)" },
        ]}
      />
    </div>
  );
}
